import React from "react";
import PageHeader from "../../common/PageHeader";
import { Face2, AddOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import { GlobalContext } from "../../context/GlobalContext";
import InfluencerCard from "./components/InfluencerCard";
import GenerateImageView from "./components/GenerateImageView";
import { UserContext } from "../../context/UserContext";
import { mediaSrc } from "../../utils/mediaSrc";
import { POYO_TXT2IMG_MODELS } from "../../constants/poyoTxt2imgModels";
import { fetchInfModels, invalidateInfModelsCache } from "./infModelsApi";

const GALLERY_PAGE_SIZE = 12;

function absoluteUrl(stored) {
  const display = mediaSrc(stored) || String(stored || "").trim();
  if (!display) return "";
  if (/^https?:\/\//i.test(display)) return display;
  if (/^https?:\/\//i.test(String(stored || ""))) return String(stored).trim();
  if (typeof window === "undefined") return display;
  const path = display.startsWith("/") ? display : `/${display}`;
  return `${window.location.origin}${path}`;
}

const Influencers = ({ lang }) => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { getUserData } = React.useContext(UserContext);

  const [view, setView] = React.useState("gallery"); // gallery | generate
  const [editSeed, setEditSeed] = React.useState(null);
  const [influencers, setInfluencers] = React.useState([]);
  const [visibleCount, setVisibleCount] = React.useState(GALLERY_PAGE_SIZE);
  const [kieModels, setKieModels] = React.useState(POYO_TXT2IMG_MODELS);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });
  const loadMoreSentinelRef = React.useRef(null);

  const visibleInfluencers = React.useMemo(
    () => influencers.slice(0, visibleCount),
    [influencers, visibleCount],
  );
  const hasMore = visibleCount < influencers.length;

  React.useEffect(() => {
    setVisibleCount(GALLERY_PAGE_SIZE);
  }, [influencers.length]);

  React.useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((n) => Math.min(n + GALLERY_PAGE_SIZE, influencers.length));
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, influencers.length, visibleCount]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchInfluencers = React.useCallback(async () => {
    try {
      const res = await fetchInfModels(hitAxios);
      if (res?.data?.success) {
        setInfluencers(res.data.data || []);
      }
    } catch {
      /* ignore */
    }
  }, [hitAxios]);

  const fetchKieModels = React.useCallback(async () => {
    try {
      const res = await hitAxios({
        path: "/api/inf/kie-models",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) {
        const list = res.data.data?.models;
        const provider = String(res.data.data?.provider || "").toLowerCase();
        const useApiList =
          Array.isArray(list) &&
          list.length &&
          (provider === "poyo" || list.every((m) => !String(m.id || "").includes("/")));
        if (useApiList) {
          setKieModels(
            list.map((m) => ({
              ...m,
              name: String(m.name || m.label || m.id || "")
                .replace(/\s*[-–—:]?\s*text[\s-]*to[\s-]*image\b/gi, "")
                .replace(/\s{2,}/g, " ")
                .trim(),
            })),
          );
        } else {
          setKieModels(POYO_TXT2IMG_MODELS);
        }
      }
    } catch {
      setKieModels(POYO_TXT2IMG_MODELS);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    fetchInfluencers();
    fetchKieModels();
  }, [fetchInfluencers, fetchKieModels]);

  React.useEffect(() => {
    if (view === "gallery") {
      fetchInfluencers();
      setEditSeed(null);
    }
  }, [view, fetchInfluencers]);

  const handleRename = async (id, name) => {
    const res = await hitAxios({
      path: "/api/inf/rename_model",
      post: true,
      admin: false,
      obj: { id, name },
    });
    if (res?.data?.success) {
      showSnackbar(res.data.msg || "Renamed", "success");
      invalidateInfModelsCache();
      fetchInfluencers();
    } else {
      showSnackbar(res?.data?.msg || "Could not rename", "error");
    }
  };

  const handleDelete = async (id) => {
    const res = await hitAxios({
      path: "/api/inf/delete_model",
      post: true,
      admin: false,
      obj: { id },
    });
    if (res?.data?.success) {
      showSnackbar(res.data.msg, "success");
      invalidateInfModelsCache();
      fetchInfluencers();
      getUserData?.();
    } else {
      showSnackbar(
        res?.data?.msg || lang?.errorDeletingCharacter || "Error deleting image",
        "error",
      );
    }
  };

  const openGenerate = () => {
    setEditSeed(null);
    setView("generate");
  };

  const openEditInStudio = (influencer, { mode = "edit" } = {}) => {
    const raw = influencer.photo_url_raw || influencer.photo_url || influencer.photoUrl;
    const abs = absoluteUrl(raw);
    if (!abs) return;
    setEditSeed({
      referenceUrl: abs,
      previewUrl: mediaSrc(raw) || abs,
      name: influencer.name || "",
      prompt: influencer.prompt || "",
      mode,
      seedId: influencer.id,
    });
    setView("generate");
  };

  if (view === "generate") {
    return (
      <GenerateImageView
        lang={lang}
        models={kieModels}
        initialReference={editSeed}
        studioHistory={influencers}
        onBack={() => setView("gallery")}
        onGalleryRefresh={fetchInfluencers}
      />
    );
  }

  return (
    <div>
      <PageHeader
        icon={Face2}
        primaryAction={
          <Button
            variant="contained"
            size="large"
            startIcon={<AddOutlined />}
            onClick={openGenerate}
          >
            {lang?.generateImageCta || "Generate Image"}
          </Button>
        }
        title={lang?.imageStudioNav || "Image Studio"}
        subtitle={
          lang?.manageInfluencers || "Generate and manage AI images"
        }
      />

      <Grid container spacing={2} mt={0.5}>
        {influencers.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">
              {lang?.noInfluencers || "No images yet. Generate one to get started."}
            </Alert>
          </Grid>
        )}
        {visibleInfluencers.map((influencer) => {
          const raw = influencer.photo_url ?? influencer.photoUrl;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={influencer.id}>
              <InfluencerCard
                lang={lang}
                influencer={{
                  ...influencer,
                  photo_url: raw ? mediaSrc(raw) : null,
                  photo_url_raw: raw || null,
                }}
                onDelete={handleDelete}
                onRename={handleRename}
                onEditInStudio={openEditInStudio}
              />
            </Grid>
          );
        })}
        {hasMore ? (
          <Grid item xs={12}>
            <Box
              ref={loadMoreSentinelRef}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                py: 2,
                color: "text.secondary",
              }}
            >
              <CircularProgress size={18} thickness={5} />
              <Typography variant="caption" color="text.secondary">
                {lang?.imageStudioLoadMoreGallery ||
                  `Loading more… (${visibleCount} of ${influencers.length})`}
              </Typography>
            </Box>
          </Grid>
        ) : null}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Influencers;
